import styles from './donationType.module.scss';
import { Card, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { object, string, ValidationError } from 'yup';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import {
   CommonButton,
   CommonOutlinedButton,
   CustomInputLabel,
   CustomLeftIcon,
   CustomRequiredInputLabel,
   CustomTextFeild,
} from '../../common/StyledComponents/StyledFields';
import toast, { Toaster } from 'react-hot-toast';
import { isNil } from 'lodash';
import ErrorPopup from '../../common/ErrorPopup';
import constants from '../../../config/constants.json';
import SuccessPopup from '../../devotee/donation/successPopup';
import LOCALE from '../../../config/en-us.json';
import commonStyles from '../../../App.module.scss';
import apiPath from '../../../config/apiPaths.json';
import { ClipLoader } from 'react-spinners';

type ErrorMessageTypes = {
   name: string;
   description: string;
};

interface Typdonation {
   selectData?: any;
   showAddDonationType?: any;
   handleBack?: () => void;
}

const DonationType = (props: Typdonation) => {
   const { selectData } = props || {};
   const [isOpen, setIsOpen] = useState<boolean>(false);
   const formFields = {
      name: '',
      description: '',
   };
   const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
      name: '',
      description: '',
   });
   const [formData, setFormData] = useState({
      ...formFields,
   });
   const [submitLoading, setSubmitLoading] = useState(false);
   const validationSchema = object().shape({
      name: string().required('Name is required'),
      description: string().required('Description is required'),
   });

   useEffect(() => {
      saveApiCall();
   }, []);
   const handleSave = async (): Promise<void> => {
      const errors: ErrorMessageTypes = {
         name: '',
         description: '',
      };
      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         saveApiCall();
      } catch (error: any) {
         // Handle validation errors
         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };

   useEffect(() => {
      updateData();
   }, [selectData]);

   const updateData = () => {
      setFormData({
         name: selectData?.name || '',
         description: selectData?.description || '',
      });
   };
   const handleUpdate = async (): Promise<void> => {
      const errors: ErrorMessageTypes = { ...formFields };
      try {
         // Validate the form data against the validation schema
         await validationSchema.validate(formData, { abortEarly: false });
         // If validation passes, you can submit the form
         setErrorMessage(errors);
         // Call your API or perform other actions here
         updateDonationTypeApiCall();
      } catch (error: any) {
         // Handle validation errors

         if (error instanceof ValidationError) {
            error.inner.forEach((e: any) => {
               if (e.path) {
                  errors[e.path as keyof ErrorMessageTypes] = e.message;
               }
            });
         }
         setErrorMessage(errors);
      }
   };
   const updateDonationTypeApiCall = () => {
      setSubmitLoading(true);
      const reqBody = {
         name: formData.name,
         description: formData.description,
         id: selectData?.id,
      };
      apiRequest({
         method: 'PUT',
         endpoint: `${apiPath.donationTypeUpdate}/${selectData?.id}`,
         body: JSON.stringify(reqBody),
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.DONATION_TYPE;
            toast.custom(
               <SuccessPopup
                  headerMessage="Details Update"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         })
         .catch((err) => {
            toast.custom(
               <ErrorPopup
                  showMessage={catchErrorMessageHandler(err)}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
               {
                  duration: constants.TOAST_POPUP_DURATION,
               },
            );
            setSubmitLoading(false);
         });
   };

   const saveApiCall = () => {
      setSubmitLoading(true);
      const reqBody = {
         name: formData.name,
         description: formData.description,
      };
      apiRequest({
         method: 'POST',
         endpoint: apiPath.donationTypeUpdate,
         body: JSON.stringify(reqBody),
         includeToken: true,
      })
         .then((res) => {
            const successMessage = LOCALE.DONATION_TYPE_ADDED;
            toast.custom(
               <SuccessPopup
                  headerMessage="Donation Type"
                  showMessage={successMessage}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
               />,
            );
            setSubmitLoading(false);
         })
         .catch((err) => {
            console.log(err);
            setSubmitLoading(false);
         });
   };

   const handleFieldChange = (field: string, value: any) => {
      setFormData({
         ...formData,
         [field]: value,
      });
   };
   const handleCancel = () => {
      setFormData({
         ...formFields,
      });
   };
   const handleReset = () => {
      updateData();
   };
   const handleButtons = () => {
      return (
         <div>
            {!isNil(selectData?.id) ? (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleReset}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleUpdate}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Update</span>
                        </>
                     ) : (
                        'Update'
                     )}
                  </CommonButton>
               </>
            ) : (
               <>
                  <button className={commonStyles.cancelButton} onClick={handleCancel}>
                     Reset
                  </button>
                  <CommonButton className={commonStyles.saveButton} onClick={handleSave}>
                     {submitLoading ? (
                        <>
                           <ClipLoader size={20} color="#fff" />
                           <span style={{ marginLeft: '8px' }}>Add</span>
                        </>
                     ) : (
                        'Add'
                     )}
                  </CommonButton>
               </>
            )}
         </div>
      );
   };
   const handleback = () => {
      props.handleBack?.();
   };

   const handleKeyPress = (e: any) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         if (selectData?.id) {
            handleUpdate();
         } else {
            handleSave();
         }
      }
   };

   const validateDonationName = (value: string) => {
      const regex = /^[a-zA-Z0-9\s]*$/;
      return regex.test(value);
   };
   const addDonationType = () => {
      return (
         <div>
             <Grid sx={{ display: "flex" }} onClick={handleback}>
               <span style={{
                  marginTop: "6px",
                  marginRight: "7px"
               }}>
                  <CustomLeftIcon />
               </span>
            <Typography
               style={{
                  color: '#414141',
                  fontFamily: 'Lato',
                  fontSize: '24px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '140%',
               }}
            >
               Add Donation Type{' '}
            </Typography>
            </Grid>
            <form onKeyDown={handleKeyPress}>
               <Card className={styles.cardConatiner}>
                  <Grid
                     sx={{
                        marginTop: '0px',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '20px',
                     }}
                  >
                     <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Grid xs={12} sm={3} item>
                           {CustomRequiredInputLabel('Name', true)}
                           <CustomTextFeild
                              fullWidth
                              required
                              value={formData.name}
                              onChange={(e) => {
                                 const value = e.target.value;
                                 if (validateDonationName(value)) {
                                    setErrorMessage({ ...errorMessage, name: '' });
                                    handleFieldChange('name', value);
                                 }
                              }}
                              className={
                                 errorMessage.name ? styles.errorTextField : styles.textField
                              }
                           />
                           {errorMessage.name && (
                              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                 {errorMessage.name}
                              </Typography>
                           )}
                        </Grid>
                        <Grid spacing={2} item>
                           <Grid xs={12} sm={4} item>
                              {CustomRequiredInputLabel('Description', true)}
                              <textarea
                                 className={
                                    errorMessage.description
                                       ? styles.description
                                       : styles.descriptionText
                                 }
                                 value={formData?.description}
                                 onChange={(e) => {
                                    handleFieldChange('description', e.target.value);
                                    setErrorMessage({ ...errorMessage, description: '' });
                                 }}
                              />
                              {errorMessage.description ? (
                                 <Typography sx={{ color: 'red', fontSize: '12px' }}>
                                    {errorMessage.description}
                                 </Typography>
                              ) : (
                                 ''
                              )}
                           </Grid>
                        </Grid>
                     </Grid>
                  </Grid>
               </Card>
            </form>
            {handleButtons()}
         </div>
      );
   };

   return (
      <div className={styles.mainContainer}>
         <Toaster />
         {addDonationType()}
      </div>
   );
};

export default DonationType;
